/**
 * Chronicle Memory Store
 * Persists unlocked hidden flags per chronicle across playthroughs.
 * Stored separately from game saves — never cleared by starting over.
 *
 * ── In-memory cache (2026-05) ─────────────────────────────────────
 * Pre-fix `read()` did a fresh `localStorage.getItem(STORAGE_KEY)`
 * + `JSON.parse(raw)` on EVERY call. The home page calls
 * `getUnlockedFlags(c.id)` for every chronicle (RegionMarker reads it
 * twice per chronicle, and `useChronicleProgress` iterates every
 * chronicle for the Archivist's Ledger) — roughly 60–90 reads per
 * render, each doing a synchronous storage hit + JSON parse. That
 * was a measurable contributor to the user-reported app-wide lag.
 *
 * Now: read() parses once and caches. Writes update the cache
 * in-place so the next read is consistent without a re-parse. A
 * `storage` event listener handles the rare cross-tab case (user
 * opens the game in two tabs and unlocks something in one) by
 * invalidating the cache so the next read picks up the fresh
 * write.
 */

const STORAGE_KEY = "chronicles_memory_flags";

type MemoryStore = Record<string, string[]>; // chronicleId → flagId[]

let cache: MemoryStore | null = null;

function read(): MemoryStore {
  if (cache !== null) return cache;
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    cache = raw ? JSON.parse(raw) : {};
  } catch {
    cache = {};
  }
  return cache!;
}

function write(store: MemoryStore) {
  cache = store;
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  }
}

// Cross-tab invalidation: another tab wrote to the memory store, so
// our cached snapshot is stale. Drop it and let the next read()
// re-parse from storage. (storage events only fire in OTHER tabs,
// not the one that performed the write, so this is purely a
// cross-tab consistency mechanism — same-tab writes go through
// write() above which keeps the cache in sync directly.)
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY || e.key === null /* clear() */) {
      cache = null;
    }
  });
}

/**
 * Test-only: drop the in-memory cache so the next read re-parses
 * from localStorage. Production code does NOT need this — same-tab
 * writes go through write() which keeps the cache in sync, and
 * cross-tab writes invalidate via the `storage` listener above.
 * The chronicle-memory test suite uses `localStorage.clear()` in
 * beforeEach to reset between tests; that clear doesn't fire a
 * storage event in the same tab, so the cache would otherwise hold
 * stale data from the previous test.
 */
export function _resetMemoryCacheForTests() {
  cache = null;
}

/** Get all unlocked memory flags for a chronicle */
export function getUnlockedFlags(chronicleId: string): string[] {
  return read()[chronicleId] || [];
}

/** Persist newly unlocked flags (deduplicates automatically) */
export function unlockFlags(chronicleId: string, flagIds: string[]) {
  const store = read();
  const existing = new Set(store[chronicleId] || []);
  let changed = false;
  for (const id of flagIds) {
    if (!existing.has(id)) {
      existing.add(id);
      changed = true;
    }
  }
  if (changed) {
    store[chronicleId] = Array.from(existing);
    write(store);
  }
}
